import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import analytics from '../../analytics/Analytics';
import LoginSplashScreen from './LoginSplashScreen';
import './AuthenticationScreen.css';

/**
 * Legacy-lane sign-in entry point.
 *
 * Reached only when `ClerkStatusBootstrap` resolves the rollout to `LEGACY`
 * (`DISABLED`, or `INTERNAL` without a mountable Clerk). During `BETA`/`ACTIVE` the
 * backend rejects these credentials with `403 legacy_auth_unavailable` and the
 * bootstrap renders the Clerk lane or the service-unavailable screen instead — so
 * this form is never shown in a state where it cannot succeed.
 *
 * New flow:
 * - **Sign In** pops the email/password form below.
 * - **Sign Up** goes to the beta-key page (`onBetaKeyFlow` -> `betaKeyValidation`),
 *   which is the legacy admission path: validate a key, then register. Requesting a
 *   key is offered from inside the popup rather than as a third splash button, since
 *   the approved splash has exactly two calls to action.
 *
 * NO SOCIAL PROVIDERS HERE. "Sign in with Google or other" is a Clerk capability and
 * belongs to `ClerkAuthenticationScreen`. This lane has no OAuth backend, so
 * rendering a provider button here would be a dead affordance.
 */
function AuthenticationScreen({ onLogin, onBetaKeyFlow, onRequestBetaKey, initialError = '' }) {
    const { login } = useAuth();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [loginForm, setLoginForm] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // `initialError` describes the ARRIVAL (e.g. "your session ended"), not the
    // popup form, so it stays on the splash and is not cleared by form edits.
    const canSubmit = Boolean(loginForm.email) && Boolean(loginForm.password) && !isLoading;

    const handleLoginChange = (e) => {
        setLoginForm({
            ...loginForm,
            [e.target.name]: e.target.value
        });
    };

    const openSignIn = () => {
        analytics.trackButtonClick('sign_in', 'authentication_screen');
        analytics.trackAuthUIAction('sign_in_opened', 'authentication_screen');
        setIsPopupOpen(true);
    };

    const closeSignIn = () => {
        setIsPopupOpen(false);
        setError('');
        // Never retain a typed password in component state after dismissal.
        setLoginForm({ email: '', password: '' });
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Track login attempt
        analytics.trackAuthUIAction('login_attempt', 'login_form', 'password');

        try {
            const result = await login({
                email: loginForm.email,
                password: loginForm.password
            });

            if (result.success) {
                console.log('AuthenticationScreen: Login successful');

                // Track successful login
                analytics.trackAuthUIAction('login_success', 'login_form', 'password');

                // Drop the submitted credentials as soon as they are spent.
                setLoginForm({ email: '', password: '' });
                setIsPopupOpen(false);

                // Call onLogin callback if provided for additional routing logic
                if (onLogin) {
                    onLogin(result.user);
                }
                // AuthProvider will handle the authentication state change
            } else {
                // Track login failure
                analytics.trackAuthUIAction('login_failure', 'login_form', 'password');
                analytics.trackError('login_failed', result.message || 'Login failed', {
                    source: 'AuthenticationScreen',
                    method: 'password'
                });

                setError(result.message || 'Login failed');
            }
        } catch (error) {
            console.error('AuthenticationScreen: Login error:', error);

            // Track login error
            analytics.trackAuthUIAction('login_error', 'login_form', 'password');
            analytics.trackError('login_error', error.message, {
                source: 'AuthenticationScreen',
                method: 'password',
                error_type: 'exception'
            });

            setError('Login failed. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    /*
     * NAVIGATION CALLBACKS FAIL LOUDLY.
     *
     * These two buttons exist only to leave this screen. If a caller renders this
     * component without wiring the route, the old code silently did nothing and the
     * button looked broken with no diagnostic — the user is stuck on a screen with
     * no other way forward. Throwing surfaces the wiring bug at the click instead of
     * presenting a dead control. `onLogin` is genuinely optional by contrast: the
     * popup authenticates through `useAuth().login()` and `AuthProvider` propagates
     * the state change, so a caller that needs no extra routing can omit it.
     */
    const handleRequestBetaKey = () => {
        if (typeof onRequestBetaKey !== 'function') {
            throw new Error(
                'AuthenticationScreen: "Request a beta key" was pressed but no ' +
                `onRequestBetaKey route was provided (received ${typeof onRequestBetaKey}).`
            );
        }

        analytics.trackAuthUIAction('beta_key_request', 'authentication_screen');
        analytics.trackButtonClick('request_beta_key', 'authentication_screen');

        onRequestBetaKey();
    };

    /**
     * Sign Up — straight to the beta-key page.
     *
     * Mythic is in closed beta, so there is no open registration to send anyone to;
     * a key is the gate. `onBetaKeyFlow` is the router's `betaKeyValidation` route,
     * which validates the key (read-only — it does not spend it) before handing off
     * to `UserRegistration`.
     */
    const handleSignUp = () => {
        if (typeof onBetaKeyFlow !== 'function') {
            throw new Error(
                'AuthenticationScreen: "Sign Up" was pressed but no onBetaKeyFlow ' +
                `route to the beta-key page was provided (received ${typeof onBetaKeyFlow}).`
            );
        }

        analytics.trackAuthUIAction('beta_key_entry', 'authentication_screen');
        analytics.trackButtonClick('sign_up', 'authentication_screen');

        onBetaKeyFlow();
    };

    return (
        <LoginSplashScreen
            onSignIn={openSignIn}
            onSignUp={handleSignUp}
            error={initialError}
            isPopupOpen={isPopupOpen}
            onPopupClose={closeSignIn}
            popupTitle="Sign In"
            popupSize="sm"
            popup={
                <form className="auth-login" onSubmit={handleLoginSubmit}>
                    <label className="auth-login__label" htmlFor="email">
                        Email
                    </label>
                    <input
                        className="auth-login__input"
                        type="email"
                        id="email"
                        name="email"
                        value={loginForm.email}
                        onChange={handleLoginChange}
                        required
                        disabled={isLoading}
                        autoComplete="email"
                        placeholder="Enter your email"
                    />

                    <label className="auth-login__label" htmlFor="password">
                        Password
                    </label>
                    <input
                        className="auth-login__input"
                        type="password"
                        id="password"
                        name="password"
                        value={loginForm.password}
                        onChange={handleLoginChange}
                        required
                        disabled={isLoading}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                    />

                    {error && (
                        <p className="auth-login__error" role="alert">
                            {error}
                        </p>
                    )}

                    <button
                        className="auth-login__submit"
                        type="submit"
                        disabled={!canSubmit}
                    >
                        {isLoading ? 'Signing in…' : 'Sign In'}
                    </button>

                    {/*
                      * Secondary, in-popup route for someone who has no key yet. It is
                      * deliberately not a third splash button — the approved splash has
                      * exactly two calls to action.
                      */}
                    <button
                        className="auth-login__secondary"
                        type="button"
                        onClick={handleRequestBetaKey}
                        disabled={isLoading}
                    >
                        Need a beta key? Request one
                    </button>
                </form>
            }
        />
    );
}

export default AuthenticationScreen;
